import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { parseMarkdown } from 'src/app/utils/markdown.utils';
import { SiCollapsiblePanelComponent } from '@siemens/element-ng/accordion';
import { SiTooltipDirective } from '@siemens/element-ng/tooltip';
import { PanelData } from '../../../../interfaces/panel-data.interface';
import { IStreamingMessageV2 } from 'src/app/interfaces/streaming-messages.interface';

@Component({
  selector: 'app-editor-support-hub',
  imports: [CommonModule, SiCollapsiblePanelComponent, SiTooltipDirective],
  templateUrl: './editor-support-hub.component.html',
  styleUrl: './editor-support-hub.component.scss',
})
export class EditorSupportHubComponent implements OnChanges {
  panels: PanelData[] = [];
  @Input() executeCommandList: any[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['executeCommandList']) return;

    const commands = changes['executeCommandList'].currentValue ?? [];
    this.panels = commands
      .filter((cmd: any) => this.isCompletedCommandWithTextContent(cmd))
      .sort((a: any, b: any) => {
        const tsA = this.findTextContentMessage(a.streamingMessages)?.timestamp ?? '';
        const tsB = this.findTextContentMessage(b.streamingMessages)?.timestamp ?? '';
        return new Date(tsB as string).getTime() - new Date(tsA as string).getTime();
      })
      .map((cmd: any, index: number) => this.createPanel(cmd, index));
  }

  copyPanelContent(panel: PanelData, event: Event): void {
    event.stopPropagation();
    const text = `"${panel.title}"\n\n${panel.content}`;
    navigator.clipboard.writeText(text);
  }

  getPanelHeading(panel: PanelData): string {
    return `"${panel.title}"`;
  }

  getPanelTimestamp(panel: PanelData): string {
    return `[${panel.timestamp}]`;
  }

  getParsedContent(content: string): string {
    if (!content || typeof content !== 'string') return '';
    return parseMarkdown(content);
  }

  private isCompletedCommandWithTextContent(cmd: any): boolean {
    return cmd.currentStreamingState === 'run_complete' && 
           this.hasTextContentMessage(cmd.streamingMessages);
  }

  private hasTextContentMessage(messages: IStreamingMessageV2[]): boolean {
    return (messages ?? []).some(this.isTextContentMessage);
  }

  private isTextContentMessage(msg: IStreamingMessageV2): boolean {
    return msg.content_type === 'text' && 
           msg.content !== null && 
           typeof msg.content === 'object' && 
           'text' in msg.content;
  }

  private findTextContentMessage(messages: IStreamingMessageV2[]): IStreamingMessageV2 | undefined {
    return (messages ?? []).find(this.isTextContentMessage);
  }

  private createPanel(cmd: any, index: number): PanelData {
    const textMessage = this.findTextContentMessage(cmd.streamingMessages);
    const timestamp = typeof textMessage?.timestamp === 'string' ? textMessage.timestamp : undefined;
    return {
      iteration: index + 1,
      title: cmd.inputMessage ?? '',
      icon: 'element-document',
      content: (textMessage?.content as any)?.text ?? '',
      timestamp: this.formatTimestamp(timestamp)
    };
  }

  private formatTimestamp(timestamp?: string): string {
    if (!timestamp) return '';
    
    const date = new Date(timestamp);
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  }
}
